import {
  RuleChange,
  WithdrawalRule,
  withdrawalReviewsApi as api,
} from "api/admin/withdrawalReviews";
import useAdminAccess from "hooks/useAdminAccess";
import { currentAdminCountryScope } from "helpers/storage";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { showDanger, showSuccess } from "utilities/misc";
import {
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorState,
  LoadingRows,
  ReasonDialog,
  StatusPill,
  errorMessage,
  fieldClass,
  formatDate,
  formatLabel,
} from "../access-control/components";

export default function WithdrawalRules() {
  const { can, admin, session } = useAdminAccess();
  const globalScope =
    session?.countryScope === "all" && currentAdminCountryScope() === "all";
  const client = useQueryClient();
  const [editing, setEditing] = useState<WithdrawalRule | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("pending");
  const [decision, setDecision] = useState<{
    change: RuleChange;
    action: "approve" | "reject";
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const rules = useQuery("withdrawal-review-rules", api.rules, {
    retry: false,
  });
  const changes = useQuery(
    ["withdrawal-rule-changes", status],
    () => api.changes(status),
    { retry: false },
  );
  const refresh = () =>
    Promise.all([
      client.invalidateQueries("withdrawal-review-rules"),
      client.invalidateQueries("withdrawal-rule-changes"),
    ]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing || busy || !reason.trim()) return;
    setBusy(true);
    try {
      await api.requestChange(editing.id, {
        enabled,
        ...(editing.triggerType === "amount_threshold"
          ? { thresholdAmount: Number(threshold) }
          : {}),
        reason: reason.trim(),
      });
      setEditing(null);
      showSuccess("Rule change submitted for approval");
      await refresh();
    } catch (error) {
      showDanger(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  const review = async (reviewReason: string) => {
    if (!decision || busy) return;
    setBusy(true);
    try {
      await api.decideChange(decision.change.id, decision.action, reviewReason);
      setDecision(null);
      showSuccess("Rule change reviewed");
      await refresh();
    } catch (error) {
      showDanger(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Rules apply across markets. Changes require a different admin to approve
        them.
        {!globalScope &&
          " Select All markets with a global account to change rules."}
      </p>
      <Card title="Withdrawal review rules">
        {rules.isError ? (
          <ErrorState error={rules.error} retry={rules.refetch} />
        ) : rules.isLoading ? (
          <LoadingRows />
        ) : !rules.data?.rules.length ? (
          <EmptyState
            title="No rules"
            body="No withdrawal review rules are configured."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr>
                  {[
                    "Trigger",
                    "Source",
                    "Currency",
                    "Threshold",
                    "Enabled",
                    "",
                  ].map((h) => (
                    <th className="p-4" key={h}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rules.data.rules.map((rule) => (
                  <tr className="border-t" key={rule.id}>
                    <td className="p-4">{formatLabel(rule.triggerType)}</td>
                    <td className="p-4">{rule.source || "All"}</td>
                    <td className="p-4">{rule.currency || "All"}</td>
                    <td className="p-4">{rule.thresholdAmount ?? "—"}</td>
                    <td className="p-4">{rule.enabled ? "Yes" : "No"}</td>
                    <td className="p-4">
                      {globalScope &&
                        can("transactions.withdrawalReviewRules.create") && (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setEditing(rule);
                              setEnabled(rule.enabled);
                              setThreshold(rule.thresholdAmount ?? "");
                              setReason("");
                            }}
                          >
                            Request change
                          </Button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Card
        title="Rule change requests"
        action={
          <label className="text-sm">
            Status
            <select
              className={fieldClass}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {["pending", "approved", "rejected"].map((v) => (
                <option key={v} value={v}>
                  {formatLabel(v)}
                </option>
              ))}
            </select>
          </label>
        }
      >
        {changes.isError ? (
          <ErrorState error={changes.error} retry={changes.refetch} />
        ) : changes.isLoading ? (
          <LoadingRows />
        ) : !changes.data?.changeRequests.length ? (
          <EmptyState
            title="No change requests"
            body="Requests appear here when a rule change is submitted."
          />
        ) : (
          <ul className="divide-y">
            {changes.data.changeRequests.map((change) => (
              <li className="p-5 space-y-3" key={change.id}>
                <div className="flex flex-wrap justify-between gap-2">
                  <b>
                    Rule #{change.ruleID} · Request #{change.id}
                  </b>
                  <StatusPill status={change.status} />
                </div>
                <p className="text-sm text-gray-600">
                  Requested by admin #{change.requestedBy} ·{" "}
                  {formatDate(change.createdAt)}
                </p>
                <p>{change.requestReason}</p>
                <p className="text-sm">
                  {change.requestedChange.enabled !== undefined &&
                    `Enabled: ${change.requestedChange.enabled ? "Yes" : "No"}. `}
                  {change.requestedChange.thresholdAmount !== undefined &&
                    `Threshold: ${change.requestedChange.thresholdAmount}.`}
                </p>
                {change.approvalReason && (
                  <p className="text-sm">Decision: {change.approvalReason}</p>
                )}
                {change.status === "pending" &&
                  globalScope &&
                  can("transactions.withdrawalReviewRules.approve") && (
                    <div className="flex gap-2">
                      {change.requestedBy === admin?.id ? (
                        <p className="text-sm text-gray-500">
                          Another admin must review your request.
                        </p>
                      ) : (
                        <>
                          {(["approve", "reject"] as const).map((action) => (
                            <Button
                              key={action}
                              variant={
                                action === "reject" ? "danger" : "primary"
                              }
                              onClick={() => setDecision({ change, action })}
                            >
                              {formatLabel(action)}
                            </Button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Drawer
        open={!!editing}
        title="Request rule change"
        close={() => !busy && setEditing(null)}
      >
        <form onSubmit={save} className="space-y-5">
          <label className="flex gap-3">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>
          {editing?.triggerType === "amount_threshold" && (
            <label className="block">
              Threshold ({editing.currency})
              <input
                className={fieldClass}
                type="number"
                min="0.01"
                step="any"
                required
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
              />
            </label>
          )}
          <label className="block">
            Reason
            <textarea
              className={fieldClass}
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <Button disabled={busy || !reason.trim()} type="submit">
            Submit for approval
          </Button>
        </form>
      </Drawer>
      <ReasonDialog
        open={!!decision}
        title="Review rule change"
        description="Approval applies this rule change across markets."
        confirmLabel={
          decision?.action === "approve" ? "Approve change" : "Reject change"
        }
        danger={decision?.action === "reject"}
        busy={busy}
        close={() => !busy && setDecision(null)}
        confirm={review}
      />
    </div>
  );
}
